import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useParams, Link } from "react-router-dom";
import BASE_URL from "../../../../config/config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TIPO_OPCIONES = [
  "Ratón",
  "Teclado",
  "Pantalla",
  "Impresora",
  "Altavoces",
  "Auriculares",
  "Dock",
  "Webcam",
  "Micrófono",
  "Lector de tarjetas",
  "Hub USB",
  "Cable",
  "Adaptador",
  "Alfombrilla",
  "SAI/UPS",
  "Tablet",
  "Móvil",
  "Otro",
];

const TIPO_ESTADO = ["Activo", "En reparación", "No activo"];

const PerifericosManager = () => {
  const { id: equipoIdParam } = useParams();
  const token = localStorage.getItem("AuthToken");
  const tokenInfo = token ? jwtDecode(token) : {};
  const isAdmin = tokenInfo?.role === "admin";

  const [perifericos, setPerifericos] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [equipoHeader, setEquipoHeader] = useState(null);
  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const [form, setForm] = useState({
    marca: "",
    modelo: "",
    numero_serie: "",
    tipo: "",
    estado: "Activo",
    notas: "",
    equipoId: "",
  });

  const [editId, setEditId] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: token || "",
      "Content-Type": "application/json",
    }),
    [token]
  );

  const fetchPerifericos = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${BASE_URL}/perifericos/`, { headers });
      setPerifericos(data || []);
    } catch (err) {
      console.error("Error al obtener periféricos:", err);
      toast.error("Error al obtener periféricos.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipos = async () => {
    try {
      const { data } = await axios.get(`${BASE_URL}/equipos/`, { headers });
      setEquipos(data || []);
    } catch (err) {
      console.error("Error al obtener equipos:", err);
      toast.error("Error al obtener equipos.");
    }
  };

  const fetchEquipoHeader = async (idEquipo) => {
    if (!idEquipo) {
      setEquipoHeader(null);
      return;
    }
    try {
      const { data } = await axios.get(`${BASE_URL}/equipos/${idEquipo}`, {
        headers,
      });
      setEquipoHeader(data || null);
    } catch (err) {
      console.error("Error al obtener el equipo:", err);
      toast.error("No se pudo cargar la info del equipo.");
      setEquipoHeader(null);
    }
  };

  useEffect(() => {
    fetchEquipos();
    fetchPerifericos();
    // 
  }, []);

  useEffect(() => {
    setForm((prev) => ({ ...prev, equipoId: equipoIdParam || "" }));
    if (equipoIdParam) fetchEquipoHeader(equipoIdParam);
    // 
  }, [equipoIdParam]);

  const filtered = useMemo(() => {
    let list = perifericos;
    if (equipoIdParam) {
      list = list.filter((p) => String(p.equipoId) === String(equipoIdParam));
    }
    const q = searchText.toLowerCase();
    return list.filter((p) =>
      [
        p.marca,
        p.modelo,
        p.numero_serie,
        p.tipo,
        p.estado,
        p?.equipo?.modelo,
        p?.equipo?.marca,
        p?.equipo?.numero_serie,
        p.notas,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [perifericos, searchText, equipoIdParam]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const pageItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = () => {
    setForm({
      marca: "",
      modelo: "",
      numero_serie: "",
      tipo: "",
      estado: "Activo",
      notas: "",
      equipoId: equipoIdParam || "",
    });
    setEditId(null);
    toast.info("Formulario reiniciado.");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      toast.error("Acceso restringido a administradores.");
      return;
    }

    try {
      if (editId) {
        await axios.put(`${BASE_URL}/perifericos/${editId}`, form, { headers });
        toast.success("Periférico actualizado correctamente.");
      } else {
        await axios.post(`${BASE_URL}/perifericos/`, form, { headers });
        toast.success("Periférico creado correctamente.");
      }
      await fetchPerifericos();
      resetForm();
    } catch (err) {
      console.error("Error guardando periférico:", err);
      toast.error("Error guardando periférico. Revisa la consola.");
    }
  };

  const onEdit = (item) => {
    setEditId(item.id);
    setForm({
      marca: item.marca ?? "",
      modelo: item.modelo ?? "",
      numero_serie: item.numero_serie ?? "",
      tipo: item.tipo ?? "",
      estado: item.estado ?? "Activo",
      notas: item.notas ?? "",
      equipoId: (equipoIdParam || item.equipoId || "").toString(),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info(`Editando periférico #${item.id}`);
  };

  const onDelete = async (id) => {
    if (!isAdmin) {
      toast.error("Acceso restringido a administradores.");
      return;
    }
    if (!window.confirm("¿Seguro que deseas eliminar este periférico?")) return;

    try {
      await axios.delete(`${BASE_URL}/perifericos/${id}`, { headers });
      toast.success("Periférico eliminado correctamente.");
      await fetchPerifericos();
    } catch (err) {
      console.error("Error al eliminar periférico:", err);
      toast.error("Error al eliminar periférico.");
    }
  };

  if (!token) {
    return (
      <div className="site-width">
        <p>Inicia sesión para continuar.</p>
        <ToastContainer position="bottom-right" />
      </div>
    );
  }

  return (
    <main className="site-width">
      <div className="dashboard-content gap-perifericos">
        <h2>
          {equipoIdParam ? "Periféricos del equipo" : "Gestión de periféricos"}
        </h2>
        <p>
          {equipoIdParam ? (
            equipoHeader ? (
              <>
                Listado de periféricos asociados al equipo <b>#{equipoIdParam}</b>.<br />
                <br />
                <strong>Marca:</strong> {equipoHeader.marca}
                <br />
                <strong>Modelo:</strong> {equipoHeader.modelo}
                <br />
                <strong>Nº Serie:</strong> {equipoHeader.numero_serie || "s/n"}
                <br />
              </>
            ) : (
              `Listado de periféricos asociados al equipo #${equipoIdParam}.`
            )
          ) : (
            "Añade, edita y elimina periféricos, y asócialos a un equipo."
          )}
        </p>
        <div className="botones-perifericos" style={{ display: "flex", gap: 8 }}>
          <Link to="/dashboard/equipos">
            <button className="btn-volver">← Volver a equipos</button>
          </Link>
          {equipoIdParam && (
            <Link to="/dashboard/perifericos">
              <button className="btn-todos">Ver todos los periféricos</button>
            </Link>
          )}
        </div>

        {/* Formulario de creación/edición */}
        <form className="form-equipos" onSubmit={onSubmit}>
          <h3>
            {equipoIdParam
              ? "Añadir periférico al equipo #" + equipoIdParam
              : "Crear periférico"}
          </h3>
          <div className="grid-form-equipos">
            <div className="form-group">
              <span className="form-label">Marca</span>
              <input
                type="text"
                value={form.marca}
                onChange={(e) => setForm({ ...form, marca: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">Modelo</span>
              <input
                type="text"
                value={form.modelo}
                onChange={(e) => setForm({ ...form, modelo: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">Nº Serie</span>
              <input
                type="text"
                value={form.numero_serie}
                onChange={(e) =>
                  setForm({ ...form, numero_serie: e.target.value })
                }
              />
            </div>

            {/* Tipo */}
            <div className="form-group">
              <span className="form-label">Tipo</span>
              <select
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              >
                <option value="">— Selecciona tipo —</option>
                {form.tipo && !TIPO_OPCIONES.includes(form.tipo) && (
                  <option value={form.tipo}>{form.tipo} (actual)</option>
                )}
                {TIPO_OPCIONES.map((op) => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>
            </div>

            {/* Estado */}
            <div className="form-group">
              <span className="form-label">Estado</span>
              <select
                value={form.estado}
                onChange={(e) => setForm({ ...form, estado: e.target.value })}
                required
              >
                {form.estado && !TIPO_ESTADO.includes(form.estado) && (
                  <option value={form.estado}>{form.estado} (actual)</option>
                )}
                {TIPO_ESTADO.map((estado) => (
                  <option key={estado} value={estado}>
                    {estado}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <span className="form-label">Equipo asociado</span>
              <select
                value={form.equipoId}
                onChange={(e) => setForm({ ...form, equipoId: e.target.value })}
              >
                <option value="">— Sin asignar —</option>
                {equipos.map((eq) => (
                  <option key={eq.id} value={eq.id}>
                    #{eq.id} · {eq.marca} {eq.modelo} (
                    {eq.numero_serie || "s/n"})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <span className="form-label">Notas</span>
              <textarea
                value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {editId ? "Actualizar periférico" : "Añadir periférico"}
            </button>
            {editId && (
              <button
                type="button"
                className="btn-cancelar-edicion"
                onClick={resetForm}
              >
                Cancelar edición
              </button>
            )}
          </div>
        </form>

        {/* Filtros / búsqueda */}
        <div className="filtro-perifericos" style={{ marginTop: 24 }}>
          <input
            type="text"
            placeholder={
              equipoIdParam
                ? "Buscar entre los periféricos de este equipo…"
                : "Buscar por nombre, marca, tipo, nº serie o equipo…"
            }
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Listado */}
        <div className="overflow-tabla" style={{ marginTop: 16 }}>
          {loading ? (
            <div className="loader-fullscreen">
              <p>Cargando…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="no-items">
              {equipoIdParam
                ? "Este equipo no tiene periféricos asociados."
                : "Sin resultados"}
            </div>
          ) : (
            <div className="panel-tickets">
              <div className="perifericos-list-header">
                <p>Marca</p>
                <p>Modelo</p>
                <p>Nº Serie</p>
                <p>Tipo</p>
                <p>Estado</p>
                <p>Equipo</p>
                <p>Acciones</p>
              </div>

              <div className="perifericos-list">
                {pageItems.map((p) => (
                  <div key={p.id} className="periferico-item">
                    <div className="nombre-item">{p.marca}</div>
                    <div className="nombre-item">{p.modelo}</div>
                    <div className="nombre-item">{p.numero_serie}</div>
                    <div className="nombre-item">{p.tipo}</div>
                    <div className="nombre-item">{p.estado}</div>
                    <div className="nombre-item">
                      {p?.equipo
                        ? `#${p.equipo.id} · ${p.equipo.marca} ${p.equipo.modelo}`
                        : p.equipoId
                        ? `#${p.equipoId}`
                        : "—"}
                    </div>
                    <div className="nombre-item actions">
                      <button className="btn-ghost" onClick={() => onEdit(p)}>
                        Editar
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => onDelete(p.id)}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Paginación */}
        {filtered.length > itemsPerPage && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span>
              Página {currentPage} de {totalPages || 1}
            </span>
            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(totalPages, p + 1))
              }
              disabled={currentPage === totalPages}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Contenedor de toasts */}
      <ToastContainer position="bottom-right" />
    </main>
  );
};

export default PerifericosManager;
